import { Drawer as BaseDrawer } from '@base-ui/react/drawer';
import { clsx } from 'clsx';
import React, { useState } from 'react';
import { useIsomorphicLayoutEffect } from '@_/hooks';

export interface DialogPrimitiveProps extends React.PropsWithChildren {
  disablePointerDismissal?: BaseDrawer.Root.Props['disablePointerDismissal'];
  modal?: BaseDrawer.Root.Props['modal'];
  onAnimationEnd?: () => void;
  onOpenChange: (open: boolean) => void;
  open?: boolean;
}

const DialogPrimitive = ({
  children,
  disablePointerDismissal,
  modal = true,
  onOpenChange,
  onAnimationEnd,
  open
}: DialogPrimitiveProps) => {
  const [rootOpen, setRootOpen] = useState(false);

  // Consumers can mount with `open=true`. Start closed, then open before paint
  // so Base UI detects the transition and applies `data-starting-style`.
  useIsomorphicLayoutEffect(() => {
    setRootOpen(!!open);
  }, [open]);

  const completeOpenChange = onAnimationEnd ? () => onAnimationEnd() : undefined;

  return (
    <BaseDrawer.Root
      disablePointerDismissal={disablePointerDismissal}
      modal={modal}
      onOpenChange={(nextOpen, eventDetails) => {
        if (shouldCancelDismissal(nextOpen, eventDetails.reason, disablePointerDismissal)) {
          eventDetails.cancel();
          return;
        }

        onOpenChange(nextOpen);
      }}
      onOpenChangeComplete={completeOpenChange}
      open={rootOpen}
    >
      <BaseDrawer.VirtualKeyboardProvider>{children}</BaseDrawer.VirtualKeyboardProvider>
    </BaseDrawer.Root>
  );
};

type DialogPrimitiveOverlayBaseProps = React.ComponentPropsWithRef<typeof BaseDrawer.Backdrop>;

export interface DialogPrimitiveOverlayProps extends DialogPrimitiveOverlayBaseProps {
  color?: 'black' | 'white' | 'light' | 'dark';
}

function DialogPrimitiveOverlay({
  className,
  color = 'dark',
  ...props
}: DialogPrimitiveOverlayProps) {
  return (
    <BaseDrawer.Backdrop
      {...props}
      className={clsx(
        'fixed inset-0 z-40',
        'opacity-[calc(1-var(--drawer-swipe-progress))] transition-opacity duration-300 ease-out',
        'data-ending-style:opacity-0 data-ending-style:duration-200 data-ending-style:ease-in data-starting-style:opacity-0',
        'data-swiping:duration-0 motion-reduce:transition-none',
        color === 'black' && 'bg-black',
        color === 'white' && 'bg-white',
        color === 'light' && 'bg-black/20',
        color === 'dark' && 'bg-black/80',
        className
      )}
    />
  );
}

export type DialogPrimitiveContentAnimation = 'none' | 'responsive' | 'scale';

type DialogPrimitiveContentBaseProps = React.ComponentPropsWithRef<typeof BaseDrawer.Popup>;

export interface DialogPrimitiveContentProps extends Omit<
  DialogPrimitiveContentBaseProps,
  'render'
> {
  animation?: DialogPrimitiveContentAnimation;
  disableSwipeDismissal?: boolean;
  render?: React.ReactElement;
  style?: React.CSSProperties;
  swipeHandle?: React.ReactNode;
}

function DialogPrimitiveContent({
  animation = 'responsive',
  className,
  children,
  disableSwipeDismissal,
  ref,
  swipeHandle,
  ...props
}: DialogPrimitiveContentProps) {
  return (
    <BaseDrawer.Viewport className="pointer-events-none fixed inset-0 z-40">
      <BaseDrawer.Popup
        ref={ref}
        {...props}
        data-base-ui-swipe-ignore={disableSwipeDismissal ? '' : undefined}
        className={clsx(
          'pointer-events-auto fixed z-40 outline-none',
          'duration-300 data-ending-style:duration-200 data-ending-style:ease-in data-swiping:duration-0 motion-reduce:transition-none',
          animation === 'responsive' && [
            'transform-(--dialog-drawer-transform) transition-transform ease-panel [--dialog-drawer-transform:translateY(calc(var(--drawer-snap-point-offset)+var(--drawer-swipe-movement-y)))]',
            'data-ending-style:[--dialog-drawer-transform:translateY(100%)]',
            'data-starting-style:[--dialog-drawer-transform:translateY(100%)]',
            'sm:transform-none sm:transition-[opacity,scale] sm:ease-out',
            'sm:data-ending-style:scale-95 sm:data-ending-style:opacity-0',
            'sm:data-starting-style:scale-95 sm:data-starting-style:opacity-0'
          ],
          animation === 'scale' && [
            'transition-[opacity,scale] ease-out',
            'data-ending-style:scale-95 data-ending-style:opacity-0',
            'data-starting-style:scale-95 data-starting-style:opacity-0'
          ],
          className
        )}
      >
        {swipeHandle}
        <BaseDrawer.Content className="contents" data-base-ui-swipe-ignore="">
          {children}
        </BaseDrawer.Content>
      </BaseDrawer.Popup>
    </BaseDrawer.Viewport>
  );
}

type DialogPrimitiveCloseProps = Omit<
  React.ComponentPropsWithRef<typeof BaseDrawer.Close>,
  'render'
> & {
  asChild?: boolean;
  render?: React.ReactElement;
};

function DialogPrimitiveClose({ asChild, children, render, ...props }: DialogPrimitiveCloseProps) {
  const resolvedRender = render ?? getChildRender(asChild, children);
  const resolvedChildren = asChild ? undefined : children;

  return (
    <BaseDrawer.Close {...props} render={resolvedRender}>
      {resolvedChildren}
    </BaseDrawer.Close>
  );
}

const DialogPrimitiveDescription = BaseDrawer.Description;
const DialogPrimitivePortal = BaseDrawer.Portal;

type DialogPrimitiveTitleProps = Omit<
  React.ComponentPropsWithRef<typeof BaseDrawer.Title>,
  'render'
> & {
  asChild?: boolean;
  render?: React.ReactElement;
};

function DialogPrimitiveTitle({ asChild, children, render, ...props }: DialogPrimitiveTitleProps) {
  const resolvedRender = render ?? getChildRender(asChild, children);
  const resolvedChildren = asChild ? undefined : children;

  return (
    <BaseDrawer.Title {...props} render={resolvedRender}>
      {resolvedChildren}
    </BaseDrawer.Title>
  );
}

type DialogPrimitiveTriggerProps = Omit<
  React.ComponentPropsWithRef<typeof BaseDrawer.Trigger>,
  'handle' | 'render'
> & {
  asChild?: boolean;
  render?: React.ReactElement;
};

function DialogPrimitiveTrigger({
  asChild,
  children,
  render,
  ...props
}: DialogPrimitiveTriggerProps) {
  const resolvedRender = render ?? getChildRender(asChild, children);
  const resolvedChildren = asChild ? undefined : children;

  return (
    <BaseDrawer.Trigger {...props} render={resolvedRender}>
      {resolvedChildren}
    </BaseDrawer.Trigger>
  );
}

function shouldCancelDismissal(
  nextOpen: boolean,
  reason: string,
  disablePointerDismissal: boolean | undefined
) {
  if (nextOpen || !disablePointerDismissal) {
    return false;
  }

  return ['close-watcher', 'escape-key', 'focus-out', 'outside-press', 'swipe'].includes(reason);
}

function getChildRender(asChild: boolean | undefined, children: React.ReactNode) {
  if (!asChild) {
    return undefined;
  }

  if (!React.isValidElement(children)) {
    throw new Error('A dialog primitive with `asChild` expects a single React element.');
  }

  return children;
}

export {
  DialogPrimitive,
  DialogPrimitiveClose,
  DialogPrimitiveContent,
  DialogPrimitiveDescription,
  DialogPrimitiveOverlay,
  DialogPrimitivePortal,
  DialogPrimitiveTitle,
  DialogPrimitiveTrigger
};
